import { computeFingerprint } from './fingerprint'
import {
  isFuncMapSentinelFrame,
  resolveFuncFrame,
  type LoadedFuncMap,
  type ResolvedFuncFrame,
} from './func-map'
import type { TelErrorData } from './tel-error-data'
import type { ResourceUserInfo, TraceData } from './tel-types'

export type ClientErrorGroupingReason =
  | 'resolved-stack'
  | 'raw-stack'
  | 'message'

export interface ClientErrorGroupingFrame {
  name: string | null
  file: string | null
  line?: number
  col?: number
  foreign?: true
  sourcePosition?: boolean
}

export interface ClientErrorGroup {
  id: string
  title: string
  errorType: string
  errorMessage: string
  culpritFn: string | null
  groupingReason: ClientErrorGroupingReason
  groupingFrames: ClientErrorGroupingFrame[]
  status: 'new' | 'ongoing'
  occurrences: number
  affectedUsers: number
  affectedCompanies: number
  firstSeen: number
  firstTraceId: string | null
  firstSpanId: string | null
  firstLogId: string | null
  firstRequestId: string | null
  lastSeen: number
  lastTraceId: string | null
  lastSpanId: string | null
  lastLogId: string | null
  lastRequestId: string | null
  error: TelErrorData
}

export interface ClientErrorOccurrence {
  groupId: string
  timestamp: number
  traceId: string | null
  spanId: string | null
  logId: string | null
  requestId: string | null
  userId: string | null
  companyId: string | null
  error: TelErrorData
}

export interface ClientErrorGroupChange {
  groupId: string
  count: number
  userIds: string[]
  companyIds: string[]
  occurrence: ClientErrorOccurrence
}

export type ClientErrorEvidenceReason = 'timeout' | 'evicted' | 'no-active-unit'

export type ClientErrorEvidence =
  | { status: 'full' }
  | { status: 'partial'; reason: ClientErrorEvidenceReason }

export interface ClientErrorCreate {
  group: ClientErrorGroup
  change: ClientErrorGroupChange
  evidence: ClientErrorEvidence
  trace?: TraceData
}

export interface ClientErrorGroupSeed {
  group: Omit<
    ClientErrorGroup,
    | 'status'
    | 'occurrences'
    | 'affectedUsers'
    | 'affectedCompanies'
    | 'firstSeen'
    | 'firstTraceId'
    | 'firstSpanId'
    | 'firstLogId'
    | 'firstRequestId'
    | 'lastSeen'
    | 'lastTraceId'
    | 'lastSpanId'
    | 'lastLogId'
    | 'lastRequestId'
  >
  occurrence: ClientErrorOccurrence
}

export interface ClientStackResolverInput {
  error: TelErrorData
  originalError: unknown
  rawFrames: ClientErrorGroupingFrame[]
}

export type ClientStackResolver = (
  input: ClientStackResolverInput,
) => ClientErrorGroupingFrame[] | null | undefined

export type ClientFuncMapsLoader = () => Promise<Map<string, LoadedFuncMap>>

export function createFuncMapStackResolver(
  maps: Map<string, LoadedFuncMap>,
): ClientStackResolver {
  return ({ rawFrames }) => {
    const resolved = rawFrames.map((frame) => resolveClientFrame(maps, frame))
    return resolved.length > 0 ? resolved : null
  }
}

export async function loadFuncMapStackResolver(input: {
  load: ClientFuncMapsLoader
  onError?: (error: unknown) => void
}): Promise<ClientStackResolver | undefined> {
  try {
    return createFuncMapStackResolver(await input.load())
  } catch (error) {
    input.onError?.(error)
    return undefined
  }
}

export function createClientErrorGroupSeed(opts: {
  data: TelErrorData
  originalError: unknown
  userInfo: ResourceUserInfo
  timestamp: number
  stackResolver?: ClientStackResolver
}): ClientErrorGroupSeed {
  const errorType = opts.data.error.type || 'UnknownError'
  const errorMessage = opts.data.error.message
  const rawFrames = parseV8Stack(opts.data.error.stack)
  const resolvedFrames = resolveFrames({
    data: opts.data,
    originalError: opts.originalError,
    rawFrames,
    stackResolver: opts.stackResolver,
  })
  const groupingReason = resolvedFrames
    ? 'resolved-stack'
    : rawFrames.length > 0
      ? 'raw-stack'
      : 'message'
  const groupingFrames = resolvedFrames ?? rawFrames
  const id = computeGroupId({
    errorType,
    errorMessage,
    errorCode: opts.data.error.code,
    frames: groupingFrames,
  })
  const occurrence: ClientErrorOccurrence = {
    groupId: id,
    timestamp: opts.timestamp,
    traceId: opts.data.traceId ?? null,
    spanId: opts.data.spanId ?? null,
    logId: opts.data.logId ?? null,
    requestId: opts.data.requestId ?? null,
    userId: opts.userInfo.userId ?? null,
    companyId: opts.userInfo.companyId ?? null,
    error: opts.data,
  }

  return {
    group: {
      id,
      title: formatTitle(errorType, errorMessage),
      errorType,
      errorMessage,
      culpritFn:
        groupingFrames.find((frame) => isCulpritEligibleFrame(frame))?.name ??
        null,
      groupingReason,
      groupingFrames,
      error: opts.data,
    },
    occurrence,
  }
}

export function mergeClientErrorGroupChange(
  existing: ClientErrorGroupChange | undefined,
  next: ClientErrorGroupChange,
): ClientErrorGroupChange {
  if (!existing) return next
  return {
    groupId: existing.groupId,
    count: existing.count + next.count,
    userIds: mergeUnique(existing.userIds, next.userIds),
    companyIds: mergeUnique(existing.companyIds, next.companyIds),
    occurrence: next.occurrence,
  }
}

function resolveFrames(opts: {
  data: TelErrorData
  originalError: unknown
  rawFrames: ClientErrorGroupingFrame[]
  stackResolver?: ClientStackResolver
}): ClientErrorGroupingFrame[] | null {
  if (!opts.stackResolver) return null

  try {
    const frames = opts.stackResolver({
      error: opts.data,
      originalError: opts.originalError,
      rawFrames: opts.rawFrames,
    })
    if (!frames || frames.length === 0) return null
    return frames
  } catch {
    return null
  }
}

// Frame policy: frames are never dropped, and raw generated names are never
// trusted for funcmap-covered chunks - unresolved means name:null. Raw names
// come from the bundler and change across builds, which re-splits groups on
// every deploy. `node:*` files are foreign in every tier, including apps
// without funcmaps (raw-stack tier), so event-loop plumbing can never enter
// a fingerprint or become a culprit.
function resolveClientFrame(
  maps: Map<string, LoadedFuncMap>,
  rawFrame: ClientErrorGroupingFrame,
): ClientErrorGroupingFrame {
  if (rawFrame.file && isNodeFile(rawFrame.file)) {
    return markForeign(rawFrame)
  }

  if (!rawFrame.file || rawFrame.line === undefined) {
    return markForeign(rawFrame)
  }

  const map = findFuncMapForFile(maps, rawFrame.file)
  if (!map) return markForeign(rawFrame)

  const resolved = resolveFuncFrame(map, rawFrame.line, rawFrame.col ?? 0)
  if (!resolved || isFuncMapSentinelFrame(resolved)) {
    return markForeign({ ...rawFrame, name: null })
  }
  return funcMapFrameToClientFrame(resolved, rawFrame)
}

function funcMapFrameToClientFrame(
  resolved: ResolvedFuncFrame,
  rawFrame: ClientErrorGroupingFrame,
): ClientErrorGroupingFrame {
  const frame = {
    name: resolved.name ?? null,
    file: resolved.source?.file ?? rawFrame.file,
    line: resolved.source?.line ?? rawFrame.line,
    col: resolved.source ? resolved.source.col : rawFrame.col,
    sourcePosition: !!resolved.source,
  }
  if (resolved.source || resolved.name) return frame
  return markForeign(frame)
}

function findFuncMapForFile(
  maps: Map<string, LoadedFuncMap>,
  file: string,
): LoadedFuncMap | null {
  const normalized = normalizeChunkFile(file)
  const withoutExt = stripJsExtension(normalized)
  return (
    maps.get(normalized) ??
    maps.get(withoutExt) ??
    [...maps.entries()].find(([key]) => withoutExt.endsWith(key))?.[1] ??
    null
  )
}

function normalizeChunkFile(file: string): string {
  const withoutQuery = normalizeFile(file)
  try {
    const url = new URL(withoutQuery)
    return trimLeadingSlash(url.pathname)
  } catch {
    return trimLeadingSlash(withoutQuery)
  }
}

function stripJsExtension(file: string): string {
  return file.replace(/\.m?js$/u, '')
}

function trimLeadingSlash(file: string): string {
  return file.replace(/^\/+/u, '')
}

function computeGroupId(input: {
  errorType: string
  errorMessage: string
  errorCode?: string
  frames: ClientErrorGroupingFrame[]
}): string {
  return computeFingerprint({
    errorType: input.errorType,
    errorCode: input.errorCode,
    resolvedFrames: input.frames.map((frame) => fingerprintFrame(frame)),
    messageFallback: input.errorMessage,
  })
}

function parseV8Stack(stack: string | undefined): ClientErrorGroupingFrame[] {
  if (!stack) return []
  const frames: ClientErrorGroupingFrame[] = []
  for (const line of stack.split('\n')) {
    const parsed = parseV8StackLine(line)
    if (parsed) frames.push(parsed)
  }
  return frames
}

function parseV8StackLine(line: string): ClientErrorGroupingFrame | null {
  const trimmed = line.trim()
  if (!trimmed.startsWith('at ')) return null

  const body = trimmed.slice(3)
  const withName =
    /^(?<name>.*?) \((?<file>.*):(?<line>\d+):(?<col>\d+)\)$/.exec(body)
  if (withName?.groups) {
    const frame = {
      name: withName.groups.name || null,
      file: normalizeFile(withName.groups.file),
      line: Number(withName.groups.line),
      col: Number(withName.groups.col),
    }
    return markRawFrameClass(frame)
  }

  const withoutName = /^(?<file>.*):(?<line>\d+):(?<col>\d+)$/.exec(body)
  if (!withoutName?.groups) return null

  const frame = {
    name: null,
    file: normalizeFile(withoutName.groups.file),
    line: Number(withoutName.groups.line),
    col: Number(withoutName.groups.col),
  }
  return markRawFrameClass(frame)
}

function normalizeFile(file: string): string {
  return file.split('?')[0] ?? file
}

function isNodeFile(file: string): boolean {
  return file.startsWith('node:')
}

function markForeign(
  frame: ClientErrorGroupingFrame,
): ClientErrorGroupingFrame {
  return { ...frame, foreign: true, sourcePosition: false }
}

function markRawFrameClass(
  frame: ClientErrorGroupingFrame,
): ClientErrorGroupingFrame {
  if (!frame.file || !isNodeFile(frame.file)) return frame
  return markForeign(frame)
}

function isCulpritEligibleFrame(frame: ClientErrorGroupingFrame): boolean {
  if (!frame.name) return false
  return !frame.foreign
}

function fingerprintFrame(frame: ClientErrorGroupingFrame): {
  name: string | null
  file: string | null
  line?: number
  foreign?: true
  sourcePosition: boolean
} {
  return {
    name: frame.name,
    file: frame.file,
    line: frame.line,
    foreign: frame.foreign,
    sourcePosition: frame.sourcePosition ?? true,
  }
}

function formatTitle(errorType: string, errorMessage: string): string {
  return errorMessage ? `${errorType}: ${errorMessage}` : errorType
}

function mergeUnique(left: string[], right: string[]): string[] {
  return [...new Set([...left, ...right])]
}
